import * as React from 'react';
import { ArrowLeftShortIcon } from '../../icons/ArrowLeftShortIcon';
import { FixMeLater, ReactElementWithRef } from '../../utils';
import { DialogContent, DialogHeader } from '../Dialog';
import { IconButton } from '../IconButton';
import { OptionMenuContext, OptionMenuContextValues } from './OptionMenuContext';
import { OptionMenuViewport } from './OptionMenuViewport';

export type OptionMenuProviderProps = {
    /**
     * The content of the component.
     */
    children: ReactElementWithRef<Element>;

    /**
     * HTML attributes to be applied to the Menu Header element
     */
    HeaderProps?: React.HTMLAttributes<HTMLElement>;

    /**
     * A reference passed to the root element.
     */
    ref: React.Ref<FixMeLater>;
};
export function OptionMenuProvider(props: Readonly<OptionMenuProviderProps>) {
    const { children, HeaderProps, ref } = props;

    const [viewportDepth, setViewportDepth] = React.useState<number | null>(0);
    const [animating, setAnimating] = React.useState<boolean>(false);
    const [menuTitle, setMenuTitle] = React.useState<string | null>(null);

    const menuTitles = React.useRef<string[]>([]);

    const handleMenuOpen = React.useCallback((depth: number, title: string) => {
        setViewportDepth(depth);
        setMenuTitle(title);
        menuTitles.current.push(title);
    }, []);

    const handleMenuBack = React.useCallback(() => {
        if (viewportDepth == null) {
            return;
        }
        menuTitles.current.pop();
        setViewportDepth(viewportDepth - 1);
        setMenuTitle(menuTitles.current[menuTitles.current.length - 1]);
    }, [viewportDepth]);

    const context: OptionMenuContextValues = React.useMemo(
        () => ({
            viewportDepth,
            setViewportDepth,
            animating,
            setAnimating,
            onMenuOpen: handleMenuOpen,
            onMenuBack: handleMenuBack,
        }),
        [animating, handleMenuBack, handleMenuOpen, viewportDepth]
    );

    const showBackButton = viewportDepth !== null && viewportDepth > 0;

    return (
        <OptionMenuContext.Provider value={context}>
            <DialogHeader
                withCloseButton={true}
                onBack={showBackButton ? handleMenuBack : undefined}
                startButton={
                    showBackButton ? (
                        <IconButton onClick={handleMenuBack}>
                            <ArrowLeftShortIcon biDirectional={true} />
                        </IconButton>
                    ) : undefined
                }
                HeadingProps={{ size: 'xs' }}
                {...HeaderProps}
            >
                {menuTitle}
            </DialogHeader>
            <DialogContent disableGutters={true} ref={ref}>
                <OptionMenuViewport>{children}</OptionMenuViewport>
            </DialogContent>
        </OptionMenuContext.Provider>
    );
}
